import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DynamicViewTable from "./DynamicViewTable";
import Popup from "reactjs-popup";
import { vehicleApiConfig } from "../config/apiConfig.js";
import { groupInfo, dataObject, validate } from "../config/vehicleForm.js";
import FormColumn from "./FormColumn.js";
import { Prompt } from "react-router-dom";
import Autosuggest from "react-autosuggest";
import "./Form.css";
import "./ChallanForm.css";
import "./AutoSuggest.css";
import useForm from "./useForm";
import "./popup.css";
import "./VehicleForm.css";
import { SERVER_URL, USE_OVERLAY } from "../config/config";
import LoadingOverlay from "react-loading-overlay";
import { checkVehicleDocumentExpiry } from "../utils/documentExpiry.js";

const VehicleForm = ({ sessionObject }) => {
  const DOC_TYPES = [
    { key: "rc_book", label: "RC Book", icon: "fa-id-card" },
    { key: "insurance", label: "Insurance", icon: "fa-shield-alt" },
    { key: "puc", label: "PUC", icon: "fa-smog" },
    { key: "permit", label: "Permit", icon: "fa-certificate" },
  ];

  const ALLOWED_DOC_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];

  const EXPIRY_DOC_TYPES = ["insurance", "puc", "permit"];

  const EXPIRY_FIELD_MAP = {
    insurance: "insurance_expiry",
    puc: "puc_expiry",
    permit: "permit_expiry",
  };

  const contentStyle = {
    maxWidth: "600px",
    width: "90%",
  };

  const uploadDocContentStyle = {
    maxWidth: "850px",
    width: "90%",
    padding: "0px",
    border: "none",
    background: "transparent",
    overflow: "hidden",
  };

  const viewDocContentStyle = {
    maxWidth: "960px",
    width: "95%",
    padding: "0px",
    border: "none",
    background: "transparent",
    overflow: "hidden",
  };

  let variablesFromSession = {
    station_from: String(sessionObject.sessionVariables.branch_id),
    station_from_name: sessionObject.sessionVariables.branch_name,
    created_from: String(sessionObject.sessionVariables.branch_id),
    created_by: String(sessionObject.sessionVariables.user_id),
  };

  const myForm = useForm(
    "Vehicle",
    validate,
    { ...dataObject, ...variablesFromSession },
    vehicleApiConfig
  );

  const getAdditionalInfoForSuggestionFetch = (fieldInfoObject) => {
    // if (fieldInfoObject.name == "vehicle_no"){
    //   return {"limit": "10"};
    // }
    return null;
  };

  const checkDisabledCondition = (fieldInfo) => {
    return ""
  };

  const checkVisibilityCondition = (fieldInfo) => {
    if (
      fieldInfo.name == "entry_date"
    ) {
      return false;
    }
    return true
  };

  React.useEffect(() => {
    console.log(myForm.pageState);
  });

  // Vehicle Document States
  const [uploadDocOpen, setUploadDocOpen] = useState(false);
  const [viewDocOpen, setViewDocOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState({
    rc_book: null,
    insurance: null,
    puc: null,
    permit: null,
  });
  const [uploadExpiryDates, setUploadExpiryDates] = useState({
    insurance: "",
    puc: "",
    permit: "",
  });
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadError, setUploadError] = useState("");

  const [vehicleDocs, setVehicleDocs] = useState(null);
  const [vehicleExpiries, setVehicleExpiries] = useState({});
  const [viewLoading, setViewLoading] = useState(false);
  const [viewDocsError, setViewDocsError] = useState("");
  const [activeDocType, setActiveDocType] = useState("rc_book");
  const [imageRotation, setImageRotation] = useState(0);
  const [docFileType, setDocFileType] = useState("auto");
  const [docPreviewUrl, setDocPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [docsVersion, setDocsVersion] = useState(Date.now());

  const shownExpiryToastsRef = useRef(new Set());

  const getDocFullUrl = (docPath, docType, version = docsVersion) => {
    const baseUrl = SERVER_URL.endsWith("/") ? SERVER_URL.slice(0, -1) : SERVER_URL;
    const vNo = myForm.pageState.vehicle_no ? myForm.pageState.vehicle_no.trim() : "";
    const vQuery = version ? `?t=${version}` : "";

    if (vNo && docType) {
      return `${baseUrl}/vehicle/doc-file/${encodeURIComponent(vNo)}/${docType}${vQuery}`;
    }

    if (!docPath) return "";
    let cleanPath = docPath.replace(/^\/?api\/v1/, "");
    if (!cleanPath.startsWith("/")) {
      cleanPath = `/${cleanPath}`;
    }
    const sep = cleanPath.includes("?") ? "&" : "?";
    return `${baseUrl}${cleanPath}${version ? `${sep}t=${version}` : ""}`;
  };

  const fetchVehicleDocuments = async () => {
    const vNo = myForm.pageState.vehicle_no ? myForm.pageState.vehicle_no.trim() : "";
    if (!vNo) {
      setViewDocsError("No vehicle number found in the form.");
      setVehicleDocs(null);
      return;
    }

    setViewLoading(true);
    setViewDocsError("");
    try {
      const response = await fetch(
        `${SERVER_URL}/vehicle/documents/${encodeURIComponent(vNo)}?t=${Date.now()}`,
        { cache: "no-store" }
      );
      if (response.ok) {
        const data = await response.json();
        setVehicleDocs(data.documents || {});
        setVehicleExpiries(data.expiry_dates || {});
        showExpiryToasts(data.expiry_dates);
      } else if (response.status === 404) {
        setVehicleDocs({});
        setViewDocsError("No documents found for this vehicle.");
      } else {
        const errData = await response.json().catch(() => ({}));
        setViewDocsError(errData.detail || errData.message || `Failed to fetch documents (Status: ${response.status})`);
      }
    } catch (err) {
      setViewDocsError("Failed to connect to vehicle documents API.");
    } finally {
      setViewLoading(false);
    }
  };

  const loadExistingExpiryDates = async () => {
    const vNo = myForm.pageState.vehicle_no ? myForm.pageState.vehicle_no.trim() : "";
    if (!vNo) {
      setUploadExpiryDates({ insurance: "", puc: "", permit: "" });
      return;
    }

    try {
      const response = await fetch(
        `${SERVER_URL}/vehicle/documents/${encodeURIComponent(vNo)}`,
        { cache: "no-store" }
      );
      if (response.ok) {
        const data = await response.json();
        const ed = data.expiry_dates || {};
        setUploadExpiryDates({
          insurance: ed.insurance_expiry || "",
          puc: ed.puc_expiry || "",
          permit: ed.permit_expiry || "",
        });
        showExpiryToasts(ed);
      } else {
        setUploadExpiryDates({ insurance: "", puc: "", permit: "" });
      }
    } catch (err) {
      setUploadExpiryDates({ insurance: "", puc: "", permit: "" });
    }
  };

  const showExpiryToasts = (expiryDates = {}) => {
    if (!expiryDates) return;
    const vNo = myForm.pageState.vehicle_no ? myForm.pageState.vehicle_no.trim() : "";
    const results = checkVehicleDocumentExpiry([
      { doc_key: "insurance", expiry_date: expiryDates.insurance_expiry },
      { doc_key: "puc", expiry_date: expiryDates.puc_expiry },
      { doc_key: "permit", expiry_date: expiryDates.permit_expiry },
    ]);

    results.forEach((result) => {
      const toastKey = `${vNo}:${result.doc_key}`;
      if (shownExpiryToastsRef.current.has(toastKey)) return;
      shownExpiryToastsRef.current.add(toastKey);
      if (result.status === "expired" || result.status === "today") {
        toast.error(result.message, { toastId: toastKey });
      } else {
        toast.warning(result.message, { toastId: toastKey });
      }
    });
  };

  const openUploadPopup = () => {
    setUploadFiles({ rc_book: null, insurance: null, puc: null, permit: null });
    setUploadExpiryDates({ insurance: "", puc: "", permit: "" });
    setUploadMessage("");
    setUploadError("");
    setUploadDocOpen(true);
    loadExistingExpiryDates();
  };

  useEffect(() => {
    shownExpiryToastsRef.current = new Set();
    if (
      myForm.pageState.vehicle_id &&
      myForm.pageState.vehicle_no
    ) {
      loadExistingExpiryDates();
    }
  }, [myForm.pageState.vehicle_id]);

  useEffect(() => {
    let activeBlobUrl = null;
    const docPath = vehicleDocs && vehicleDocs[activeDocType];

    if (!docPath || !viewDocOpen) {
      setDocPreviewUrl(null);
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    setImageRotation(0);

    const fullUrl = getDocFullUrl(docPath, activeDocType, docsVersion);

    fetch(fullUrl, { cache: "no-store" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load file");
        const blob = await res.blob();
        const headerType = res.headers.get("content-type") || "";
        const isImg = headerType.includes("image") || blob.type.includes("image");

        const mimeType = headerType || (isImg ? "image/jpeg" : "application/pdf");
        const safeBlob = new Blob([blob], { type: mimeType });
        activeBlobUrl = URL.createObjectURL(safeBlob);

        setDocPreviewUrl(activeBlobUrl);
        setDocFileType(isImg ? "image" : "pdf");
      })
      .catch((err) => {
        console.error("Preview fetch error:", err);
        setDocPreviewUrl(null);
      })
      .finally(() => {
        setPreviewLoading(false);
      });

    return () => {
      if (activeBlobUrl) {
        URL.revokeObjectURL(activeBlobUrl);
      }
    };
  }, [activeDocType, vehicleDocs, viewDocOpen, docsVersion]);

  const handleFileChange = (docKey, file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_DOC_EXTENSIONS.includes(ext)) {
      setUploadError(`Invalid file type for ${docKey}. Allowed types: pdf, jpg, jpeg, png`);
      return;
    }
    setUploadError("");
    setUploadFiles((prev) => ({
      ...prev,
      [docKey]: file,
    }));
  };

  const handleRemoveFile = (docKey) => {
    setUploadFiles((prev) => ({
      ...prev,
      [docKey]: null,
    }));
    const inputEl = document.getElementById(`file-input-${docKey}`);
    if (inputEl) inputEl.value = "";
  };

  const handleUploadSubmit = async () => {
    const vNo = myForm.pageState.vehicle_no ? myForm.pageState.vehicle_no.trim() : "";
    if (!vNo) {
      setUploadError("Vehicle Number is missing.");
      return;
    }

    const hasAnyFile = Object.values(uploadFiles).some((file) => file !== null);
    const hasAnyExpiry = EXPIRY_DOC_TYPES.some(
      (dt) => uploadExpiryDates[dt] && uploadExpiryDates[dt].trim()
    );
    if (!hasAnyFile && !hasAnyExpiry) {
      setUploadError("Please select at least one document to upload.");
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadMessage("");

    try {
      const formData = new FormData();
      formData.append("vehicle_no", vNo);
      if (uploadFiles.rc_book) formData.append("rc_book", uploadFiles.rc_book);
      if (uploadFiles.insurance) formData.append("insurance", uploadFiles.insurance);
      if (uploadFiles.puc) formData.append("puc", uploadFiles.puc);
      if (uploadFiles.permit) formData.append("permit", uploadFiles.permit);
      EXPIRY_DOC_TYPES.forEach((dt) => {
        if (uploadExpiryDates[dt] && uploadExpiryDates[dt].trim()) {
          formData.append(EXPIRY_FIELD_MAP[dt], uploadExpiryDates[dt]);
        }
      });

      const uploadUrl = `${SERVER_URL}/vehicle/upload-document?vehicle_no=${encodeURIComponent(vNo)}`;
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
      });

      const resData = await response.json().catch(() => ({}));

      if (response.ok) {
        setUploadMessage(resData.message || "Documents uploaded successfully!");
        setUploadFiles({
          rc_book: null,
          insurance: null,
          puc: null,
          permit: null,
        });
        setUploadExpiryDates({ insurance: "", puc: "", permit: "" });
        DOC_TYPES.forEach((dt) => {
          const inputEl = document.getElementById(`file-input-${dt.key}`);
          if (inputEl) inputEl.value = "";
        });
        EXPIRY_DOC_TYPES.forEach((dt) => {
          const expiryEl = document.getElementById(`expiry-input-${dt}`);
          if (expiryEl) expiryEl.value = "";
        });
        setDocsVersion(Date.now());
        fetchVehicleDocuments();
      } else {
        let errMsg = "Upload failed";
        if (typeof resData.detail === "string") {
          errMsg = resData.detail;
        } else if (Array.isArray(resData.detail)) {
          errMsg = resData.detail.map((d) => d.msg || JSON.stringify(d)).join(", ");
        } else if (resData.message) {
          errMsg = resData.message;
        } else {
          errMsg = `Upload failed with status ${response.status}`;
        }
        setUploadError(errMsg);
      }
    } catch (err) {
      setUploadError("Network error while uploading documents.");
    } finally {
      setUploading(false);
    }
  };

  const formatExpiryDisplay = (dateStr) => {
    if (!dateStr) return "";
    const parts = String(dateStr).slice(0, 10).split("-");
    return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : dateStr;
  };
  
  const handleInvalidOtpCall = async () => {
    const url = SERVER_URL + "/token_verification/expire?"
                + `token=${myForm.pageState.vehicle_no}`;

    await fetch(url, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
  }

  const handleSendOTP = async () => {
    const currOtp = parseInt(myForm.pageState.otp);

    const url = SERVER_URL + "/token_verification/verify?"
                + `token=${myForm.pageState.vehicle_no}`
                + `&otp=${currOtp}`;
    let resp = await fetch(url, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if(resp.ok) {
        const respData = await resp.json();

        if(respData.flag == 1) {
            // save
            myForm.setPageState((oldState) => ({
                ...oldState,
                "otp_success": true,
            }))

            document.getElementById("send_button").click();
        }
        else {
            // if attempts Remaining == -1 and this resp was also negative
            if(myForm.pageState.otp_attempts == 1) {
                await handleInvalidOtpCall();
                window.location.reload();
            }
            else {
                myForm.setPageState((oldState) => ({
                    ...oldState,
                    "otp": "",
                    "otp_attempts": parseInt(myForm.pageState.otp_attempts) - 1,
                }))
                document.getElementById("otp").focus();
            }
        }
    }
  }

  return (
    <div className="challan-form-container">
      {
        USE_OVERLAY && (
          <LoadingOverlay
          active={myForm.overlay}
          spinner
          text="Loading your content..."
          styles={{
            wrapper: {
              // width: '400px',
              // height: '400px',
              overflow: true ? "hidden" : "scroll",
            },
          }}
        ></LoadingOverlay>
        )
      }
      <div>
        <Popup
          // trigger={<button className="button"> Open Modal </button>}
          open={myForm.pageMode == "submitted" || myForm.pageMode == "error"}
          modal
          contentStyle={contentStyle}
          closeOnDocumentClick={false}
        >
          {(close) => (
            <div className="pop-up-container">
              <div className="pop-up-header">
                {" "}
                {myForm.pageMode == "submitted" ? (
                  <div>Vehicle Saving Successful </div>
                ) : (
                  <div>Error In Vehicle Module </div>
                )}
                <div>
                  <a className="pop-up-close btn" onClick={close}>
                    &times;
                  </a>
                </div>
              </div>
              {myForm.pageMode == "submitted" ? (
                <div className="pop-up-content">
                  {" "}
                  Vehicle Master Saved Successfully:-
                  <br />
                  <div className="pop-up-fields">
                    <div className="pop-up-field">
                      <div className="pop-up-field-label">Vehicle No</div>
                      <div className="pop-up-field-value">
                        {myForm.pageState.vehicle_no}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="pop-up-content">
                  {" "}
                  Error in Vehicle Master with the following info:-
                  <br />
                  <div className="pop-up-fields">
                    <div className="pop-up-field">
                      <div className="pop-up-field-label">Error:</div>
                      <div className="pop-up-field-value">
                        {myForm.popupError}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="pop-up-actions">
                <button
                  className="pop-up-button"
                  onClick={() => {
                    if (myForm.pageMode == "submitted") {
                      // sessionObject.saveSessionVariableByField("last_bilty_no", {"last_bilty_no":myForm.pageState.bilty_no})
                      myForm.setPageState({
                        ...dataObject,
                        ...variablesFromSession,
                      });
                      myForm.setPageMode("write");
                      window.location.reload();
                      close();
                    } else {
                      myForm.setPageMode("write");
                      close();
                    }
                  }}
                >
                  Okay
                </button>
              </div>
            </div>
          )}
        </Popup>
      </div>
      <div>
      <Popup
          // trigger={<button className="button"> Open Modal </button>}
          open={myForm.pageState.pan_popup == "1"}
          modal
          contentStyle={contentStyle}
          closeOnDocumentClick={false}
        >
          {(close) => (
            <div className="pop-up-container">
              <div className="pop-up-header">Given Pan number exists 10 times, Are you sure want to proceed?</div>
              <div className="pop-up-actions">
                <button
                  className="pop-up-button"
                  onClick={() => {
                    let newState = {
                      pan_popup: "0",
                      // pan_no: "",
                      ingore_pan_limit: "1",
                    }
                    myForm.setPageState({
                        ...myForm.pageState,
                        ...newState,
                      });
                    close();
                  }}
                >
                  Yes
                </button>
                <button
                  className="pop-up-button"
                  onClick={() => {
                    let newState = {
                      pan_popup: "0",
                      pan_no: "",
                    }
                    myForm.setPageState({
                        ...myForm.pageState,
                        ...newState,
                      });
                    close();
                  }}
                >
                  No
                </button>
              </div>
            </div>
          )}
        </Popup>
      </div>
      <div>
                <Popup
                    open={myForm.pageState.save_clicked == true}
                    modal
                    contentStyle={contentStyle}
                    closeOnDocumentClick={false}
                >
                    {(close) => {
                        return (
                            <div className="pop-up-container">
                                <div className="pop-up-header">
                                    Enter OTP (Remaining attempts : {myForm.pageState.otp_attempts})
                                    <div>
                                        <a className="pop-up-close btn" onClick={() => {
                                            myForm.setPageState((pageState) => ({
                                                ...pageState,
                                                "save_clicked": false,
                                                "otp": "",
                                                "otp_attempts": "3",
                                            }))
                                            close();
                                        }}>
                                            &times;
                                        </a>
                                    </div>
                                </div>
                                <div className="popup-content">
                                    <div className="pop-up-fields">
                                        <div className="pop-up-field">
                                            <label className="form-label">OTP </label>
                                            <input
                                                className="form-input"
                                                type="text"
                                                name="otp"
                                                id="otp"
                                                value={myForm.pageState.otp}
                                                onChange={myForm.handleChange}
                                                onKeyPress={(e) => {
                                                    if (e.key == "Enter") {
                                                        document.getElementById("verified_by").focus();
                                                    }
                                                }}
                                                autoFocus={true}
                                            />
                                        </div>
                                        <div className="pop-up-field">
                                            <label className="form-label">Verified By </label>
                                            <input
                                                className="form-input"
                                                type="text"
                                                name="verified_by"
                                                id="verified_by"
                                                value={myForm.pageState.verified_by}
                                                onChange={myForm.handleChange}
                                                onKeyPress={(e) => {
                                                    if (e.key == "Enter") {
                                                        document.getElementById("send_otp").focus();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="pop-up-actions">
                                    <button
                                        className="pop-up-button"
                                        id="send_otp"
                                        // type="submit"
                                        onClick={() => {
                                            handleSendOTP();
                                            if(myForm.pageState.otp_success == true) {
                                                myForm.setPageState((pageState) => ({
                                                    ...pageState,
                                                    "save_clicked": false,
                                                    "otp": "",
                                                    "otp_attempts": "3",
                                                    "otp_success": false,
                                                }))
                                                close();
                                            }
                                        }}
                                    >
                                        Send
                                    </button>
                                </div>
                            </div>
                        );
                    }}
                </Popup>
            </div>

      {/* Upload Vehicle Document Popup */}
      <Popup
        open={uploadDocOpen}
        modal
        contentStyle={uploadDocContentStyle}
        closeOnDocumentClick={false}
      >
        {(close) => (
          <div className="vdoc-upload-modal">
            <div className="vdoc-modal-header">
              <span style={{ fontSize: "16px", fontWeight: "bold" }}>Upload Vehicle Documents</span>
              <button
                type="button"
                className="vdoc-close-btn"
                onClick={() => {
                  setUploadDocOpen(false);
                  close();
                }}
              >
                &times;
              </button>
            </div>

            <div className="vdoc-upload-body">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <span style={{ fontWeight: "bold", marginRight: "8px" }}>Vehicle No:</span>
                  <span className="vdoc-vehicle-badge">
                    {myForm.pageState.vehicle_no || "N/A"}
                  </span>
                </div>
                <button
                  type="button"
                  className="vdoc-btn vdoc-btn-secondary"
                  onClick={() => {
                    setUploadDocOpen(false);
                    setViewDocOpen(true);
                    fetchVehicleDocuments();
                  }}
                >
                  <i className="fas fa-eye"></i> View Documents
                </button>
              </div>

              {uploadMessage && (
                <div
                  style={{
                    backgroundColor: "#d4edda",
                    color: "#155724",
                    border: "1px solid #c3e6cb",
                    borderRadius: "4px",
                    padding: "10px 14px",
                    marginBottom: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <i className="fas fa-check-circle" style={{ marginRight: "6px" }}></i>
                    {uploadMessage}
                  </div>
                  <button
                    type="button"
                    className="vdoc-btn"
                    style={{ padding: "3px 8px", fontSize: "12px" }}
                    onClick={() => {
                      setUploadDocOpen(false);
                      setViewDocOpen(true);
                      fetchVehicleDocuments();
                    }}
                  >
                    View Now
                  </button>
                </div>
              )}

              {uploadError && (
                <div
                  style={{
                    backgroundColor: "#f8d7da",
                    color: "#721c24",
                    border: "1px solid #f5c6cb",
                    borderRadius: "4px",
                    padding: "10px 14px",
                    marginBottom: "12px",
                  }}
                >
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: "6px" }}></i>
                  {uploadError}
                </div>
              )}

              <div className="vdoc-upload-grid">
                {DOC_TYPES.map((dt) => {
                  const selectedFile = uploadFiles[dt.key];
                  return (
                    <div key={dt.key} className="vdoc-upload-card">
                      <div className="vdoc-card-title">
                        <i className={`fas ${dt.icon}`}></i>
                        <span>{dt.label}</span>
                      </div>
                      <div className="vdoc-file-input-wrapper">
                        <input
                          id={`file-input-${dt.key}`}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleFileChange(dt.key, e.target.files[0]);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="vdoc-btn vdoc-btn-secondary"
                          onClick={() => {
                            const el = document.getElementById(`file-input-${dt.key}`);
                            if (el) el.click();
                          }}
                        >
                          <i className="fas fa-folder-open"></i> Choose File
                        </button>
                        {selectedFile ? (
                          <>
                            <span className="vdoc-file-info" title={selectedFile.name}>
                              {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                            </span>
                            <button
                              type="button"
                              className="vdoc-remove-btn"
                              title="Remove file"
                              onClick={() => handleRemoveFile(dt.key)}
                            >
                              &times;
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: "12px", color: "#666" }}>No file chosen</span>
                        )}
                        {EXPIRY_DOC_TYPES.includes(dt.key) && (
                          <>
                            <label className="vdoc-expiry-label" htmlFor={`expiry-input-${dt.key}`}>
                              Expiry Date:
                            </label>
                            <input
                              id={`expiry-input-${dt.key}`}
                              type="date"
                              className="vdoc-expiry-input"
                              value={uploadExpiryDates[dt.key] || ""}
                              onChange={(e) => {
                                const dateValue = e.target.value;
                                setUploadExpiryDates((prev) => ({
                                  ...prev,
                                  [dt.key]: dateValue,
                                }));
                              }}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: "12px", fontSize: "12px", color: "#555" }}>
                * Allowed file extensions: <strong>pdf, jpg, jpeg, png</strong>. Re-uploading replaces any existing file.
              </div>
            </div>

            <div className="vdoc-upload-footer">
              <button
                type="button"
                className="vdoc-btn"
                disabled={uploading}
                onClick={handleUploadSubmit}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload"></i> Upload
                  </>
                )}
              </button>
              <button
                type="button"
                className="vdoc-btn vdoc-btn-secondary"
                onClick={() => {
                  setUploadDocOpen(false);
                  close();
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Popup>

      {/* View Vehicle Document Popup */}
      <Popup
        open={viewDocOpen}
        modal
        contentStyle={viewDocContentStyle}
        closeOnDocumentClick={false}
      >
        {(close) => {
          const currentDocPath = vehicleDocs && vehicleDocs[activeDocType];
          const currentDocUrl = currentDocPath ? getDocFullUrl(currentDocPath, activeDocType) : null;
          const activeDoc = DOC_TYPES.find((d) => d.key === activeDocType);
          const activeDocLabel = activeDoc ? activeDoc.label : activeDocType;

          return (
            <div className="vdoc-modal-container">
              {/* Modal Top Header */}
              <div className="vdoc-modal-header">
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                    Vehicle Documents
                  </span>
                  <span className="vdoc-vehicle-badge">
                    {myForm.pageState.vehicle_no || "N/A"}
                  </span>
                  {(["insurance", "puc", "permit"]).map((key) => {
                    const expiry = vehicleExpiries[EXPIRY_FIELD_MAP[key]];
                    return expiry ? (
                      <span key={key} className="vdoc-expiry-chip">
                        {DOC_TYPES.find((d) => d.key === key).label}: {formatExpiryDisplay(expiry)}
                      </span>
                    ) : null;
                  })}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button
                    type="button"
                    className="vdoc-btn vdoc-btn-secondary"
                    onClick={() => {
                      setDocsVersion(Date.now());
                      fetchVehicleDocuments();
                    }}
                    title="Refresh document status"
                  >
                    <i className="fas fa-sync"></i> Refresh
                  </button>
                  <button
                    type="button"
                    className="vdoc-close-btn"
                    onClick={() => {
                      setViewDocOpen(false);
                      close();
                    }}
                  >
                    &times;
                  </button>
                </div>
              </div>

              {/* Navigation Bar: Tabs on left, Actions on right */}
              <div className="vdoc-navbar">
                <div className="vdoc-tabs">
                  {DOC_TYPES.map((dt) => {
                    const isUploaded = vehicleDocs && Boolean(vehicleDocs[dt.key]);
                    const isActive = activeDocType === dt.key;
                    return (
                      <button
                        key={dt.key}
                        type="button"
                        className={`vdoc-tab-btn ${isActive ? "active" : ""}`}
                        onClick={() => setActiveDocType(dt.key)}
                      >
                        <i className={`fas ${dt.icon}`}></i>
                        <span>{dt.label}</span>
                        <span
                          className={`vdoc-badge ${
                            isUploaded ? "vdoc-badge-success" : "vdoc-badge-missing"
                          }`}
                        >
                          {isUploaded ? "UPLOADED" : "NONE"}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="vdoc-preview-actions">
                  {currentDocUrl && (
                    <>
                      <a
                        href={docPreviewUrl || currentDocUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vdoc-btn"
                        title="Open in new window"
                      >
                        <i className="fas fa-external-link-alt"></i> Full View
                      </a>
                      <a
                        href={currentDocUrl}
                        download={`${myForm.pageState.vehicle_no}_${activeDocType}`}
                        className="vdoc-btn"
                        title="Download file"
                      >
                        <i className="fas fa-download"></i> Download
                      </a>
                      {docFileType === "image" && (
                        <button
                          type="button"
                          className="vdoc-btn vdoc-btn-secondary"
                          onClick={() => setImageRotation((prev) => (prev + 90) % 360)}
                          title="Rotate 90 degrees"
                        >
                          <i className="fas fa-redo"></i> Rotate
                        </button>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    className="vdoc-btn vdoc-btn-secondary"
                    onClick={() => {
                      setViewDocOpen(false);
                      openUploadPopup();
                    }}
                    title="Upload or replace document"
                  >
                    <i className="fas fa-upload"></i> {currentDocUrl ? "Re-upload" : "Upload"}
                  </button>
                </div>
              </div>

              {/* Document Display Area - takes full remaining height */}
              <div className="vdoc-modal-body">
                {viewLoading || previewLoading ? (
                  <div className="loader-container" style={{ height: "100%" }}>
                    <div className="spinner"></div>
                    <div style={{ marginTop: "12px", fontWeight: "bold", color: "#333" }}>
                      Loading {activeDocLabel}...
                    </div>
                  </div>
                ) : currentDocUrl && docPreviewUrl ? (
                  docFileType === "image" ? (
                    <div className="vdoc-img-viewport">
                      <img
                        key={`${activeDocType}-${docPreviewUrl}`}
                        src={docPreviewUrl}
                        alt={activeDocLabel}
                        style={{
                          transform: `rotate(${imageRotation}deg)`,
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    </div>
                  ) : (
                    <iframe
                      key={`${activeDocType}-${docPreviewUrl}`}
                      src={docPreviewUrl}
                      title={activeDocLabel}
                      className="vdoc-iframe"
                    />
                  )
                ) : (
                  <div className="vdoc-empty-state">
                    <i className="fas fa-file-excel fa-4x" style={{ color: "#888", marginBottom: "16px" }}></i>
                    <h3>{activeDocLabel} Not Uploaded</h3>
                    <p>No {activeDocLabel} document is currently attached to this vehicle ({myForm.pageState.vehicle_no}).</p>
                    <button
                      type="button"
                      className="vdoc-btn"
                      style={{ padding: "8px 18px", fontSize: "14px", marginTop: "10px" }}
                      onClick={() => {
                        setViewDocOpen(false);
                        openUploadPopup();
                      }}
                    >
                      <i className="fas fa-upload"></i> Upload {activeDocLabel} Now
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        }}
      </Popup>

      <div className="form-header"> Vehicle </div>
      <div onSubmit={myForm.handleSubmit} className="form" noValidate>
        <div className="form-title">
        <div className="form-row">
            <label className="form-label">Created Date</label>
            <input
              className="form-input-vehicle-date"
              type="date"
              name="entry_date"
              placeholder=""
              disabled="disabled"
              value={myForm.pageState.entry_date}
              onChange={myForm.handleChange}
              // onKeyPress={linkBilty}
              ref={(a) => myForm.storeInputReferenceForSelect(a, "entry_date")}
              // disabled={myForm.pageMode == "view" ? "disabled" : ""}
            />
        </div>
        <div className="form-row">
            <label className="form-last_bilty">Verified By: </label>
            <label className="form-last_bilty">
            {myForm.pageState.verified_by}
            </label>
        </div>
        </div>
        <div className="form-input-content-block-0">
          <div className="form-field-left">
            <FormColumn
              groupInfo={groupInfo}
              groupName="group-1"
              checkDisabledCondition={checkDisabledCondition}
              checkVisibilityCondition={checkVisibilityCondition}
              myFormObj={myForm}
              getAdditionalInfoForSuggestionFetch={getAdditionalInfoForSuggestionFetch}
            />
          </div>
          <div className="form-field-right">
            <FormColumn
              groupInfo={groupInfo}
              groupName="group-2"
              checkDisabledCondition={checkDisabledCondition}
              checkVisibilityCondition={checkVisibilityCondition}
              myFormObj={myForm}
              getAdditionalInfoForSuggestionFetch={getAdditionalInfoForSuggestionFetch}
            />
          </div>
        </div>
        <div className="form-footer">
         
          <button
            id="send_button"
            onClick={(e) => {
                if (
                    process.env.REACT_APP_CLIENT === "bgtc" ||
                    process.env.REACT_APP_CLIENT === "guru" ||
                    sessionObject.sessionVariables.role_id == "1" ||
                    myForm.pageState.otp_success
                ) {
                    myForm.handleSubmit(e);
                } else {
                    const url = SERVER_URL + "/token_verification/generate?"
                    + `vehicle_no=${myForm.pageState.vehicle_no}`
                    + `&name=${myForm.pageState.owner_name}`
                    + `&pan_no=${myForm.pageState.pan_no}`
                    + `&tds_rate=${myForm.pageState.tds_rate}`
                    + `&declaration=${myForm.pageState.policy_no}`;
                    fetch(url, {
                        method: "POST",
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    });

                    myForm.setPageStateByField("save_clicked", true)
                }
            }}
            type="button"
            className="btn btn-primary"
            ref={(a) => myForm.storeInputReferenceForSelect(a, "save_button")}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={openUploadPopup}
          >
            Upload Document
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setViewDocOpen(true);
              fetchVehicleDocuments();
            }}
          >
            View Document
          </button>
          {/* <div className="status">{myForm.renderSubmitApiResponseStatus()}</div> */}
        </div>
      </div>
    </div>
  );
};

export default VehicleForm;
